import {
  Box,
  Text,
  Badge,
  HStack,
  Flex,
  Tooltip,
  Icon
} from "@chakra-ui/react";
import { Handle, Position, type NodeProps } from "@xyflow/react";
import { useState, useRef, useEffect } from "react";
import { StateStats, StateStatsEvent } from "../classes/state-machine/state";
import { ActionButtons } from "./parts/action-buttons";
import { StatesMachine } from "../classes/state-machine/state-machine";
import { getBlockDefinition } from "../node-catalog";
import { StateProps } from "../classes/state-machine/state";
import { CloseIcon, WarningTwoIcon } from "@chakra-ui/icons";
import { SUCCESS_DIAGNOSTICS } from "../side-panel/side-panel-states-editors/DiagnosticPanel";
import {
  ModalDetalhesErro,
  openModalDetalhesErro
} from "./parts/modal-detalhes-erro";

export interface StateNodeData {
  label: string;
  subtitle?: string;
  stats?: StateStats;
  operation: StateProps["operation"];
  stateId: number;
  isConfigPending?: boolean;
  pendingConfigCount?: number;
  isSelected?: boolean;
  hasIncomingHandle?: boolean;
  hasOutgoingHandle?: boolean;
  pipelineData: StatesMachine | undefined;
  onDelete?: () => void;
  [key: string]: unknown;
}

export function StateNode({ data }: NodeProps) {
  const [isHovered, setIsHovered] = useState(false);
  const nodeData = data as StateNodeData;
  const {
    label,
    subtitle,
    stats,
    hasIncomingHandle,
    hasOutgoingHandle,
    operation,
    isSelected,
    isConfigPending,
    pendingConfigCount,
    pipelineData,
    onDelete,
    stateId
  } = nodeData;

  const isDisabled = !!pipelineData?.id;
  const definition = getBlockDefinition(operation);
  const hasPendingConfig = !isDisabled && !!isConfigPending;
  const accentColor = hasPendingConfig
    ? "#D69E2E"
    : (definition?.accentColor ?? "#CBD5E0");

  const filteredStatsEvents =
    operation === "ANALISE_IA" && pipelineData?.getState(stateId)?.paused
      ? []
      : stats?.events || [];

  return (
    <Box
      px={3}
      py={2}
      borderRadius={14}
      bg="white"
      minW="220px"
      textAlign="left"
      fontSize="sm"
      position="relative"
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      boxShadow={
        hasPendingConfig
          ? "0 0 0 3px rgba(214, 158, 46, 0.24)"
          : isSelected
            ? "0 0 0 3px rgba(59,130,246,.2)"
            : "sm"
      }
      borderTop={`4px solid ${accentColor}`}
      borderBottom={`2px solid ${accentColor}`}
      borderLeft={`2px solid ${accentColor}`}
      borderRight={`2px solid ${accentColor}`}
    >
      {onDelete && (
        <Flex
          className="nodrag nopan"
          as="button"
          onClick={(e) => {
            e.stopPropagation();
            onDelete();
          }}
          position="absolute"
          top="-8px"
          right="-8px"
          w="20px"
          h="20px"
          borderRadius="full"
          bg="#EF4444"
          border="2px solid white"
          color="white"
          align="center"
          justify="center"
          cursor="pointer"
          opacity={isHovered || isSelected ? 1 : 0}
          transition="opacity .15s ease"
          zIndex={1}
          _hover={{ bg: "#DC2626" }}
        >
          <CloseIcon w="7px" h="7px" />
        </Flex>
      )}
      {hasIncomingHandle && (
        <Handle
          type="target"
          style={{
            border: `2px solid ${accentColor}`
          }}
          position={Position.Left}
        />
      )}

      <HStack align="center" spacing={3}>
        <Flex
          w="38px"
          h="38px"
          borderRadius="12px"
          align="center"
          justify="center"
          bg={
            hasPendingConfig ? "#FFFAEB" : (definition?.softColor ?? "#F1F5F9")
          }
          color={
            hasPendingConfig
              ? "#975A16"
              : (definition?.accentColor ?? "#64748B")
          }
          flexShrink={0}
        >
          {definition?.icon ? (
            <Icon as={definition.icon} fontSize="20px" />
          ) : null}
        </Flex>

        <Flex flexDir="column" align="flex-start" minW={0}>
          <Text fontWeight="800" color="#334155" lineHeight="1.1" mb="1">
            {label}
          </Text>
          <Text
            color="#94A3B8"
            fontSize="xs"
            fontWeight="700"
            lineHeight="1.1"
            isTruncated
          >
            {subtitle}
          </Text>

          {hasPendingConfig && (
            <Badge
              mt="5px"
              colorScheme="orange"
              fontSize="10px"
              display="flex"
              alignItems="center"
              gap="1"
            >
              <WarningTwoIcon w="10px" h="10px" /> Configurar
              {pendingConfigCount ? ` (${pendingConfigCount})` : ""}
            </Badge>
          )}
        </Flex>
      </HStack>

      {!!filteredStatsEvents.length && (
        <NodeStats
          filteredStatsEvents={filteredStatsEvents}
          nodeData={nodeData}
        />
      )}

      {hasOutgoingHandle && (
        <Handle
          type="source"
          style={{ border: `2px solid ${accentColor}` }}
          position={Position.Right}
        />
      )}
    </Box>
  );
}

function NodeStats({
  filteredStatsEvents,
  nodeData
}: {
  filteredStatsEvents: StateStatsEvent[];
  nodeData: StateNodeData;
}) {
  const scrollRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    const handler = (e: WheelEvent) => e.stopPropagation();
    el.addEventListener("wheel", handler, { passive: false });
    return () => el.removeEventListener("wheel", handler);
  }, []);

  const successCodesOrder = Object.keys(SUCCESS_DIAGNOSTICS).map(Number);

  const groupMap = new Map<number, StateStatsEvent>();
  filteredStatsEvents.forEach((e) => {
    const code = e.code ?? 400;
    const canonicalCode = SUCCESS_DIAGNOSTICS[code]?.mergedInto ?? code;
    const existing = groupMap.get(canonicalCode);
    if (existing) {
      groupMap.set(canonicalCode, {
        ...existing,
        total: existing.total + e.total
      });
    } else {
      groupMap.set(canonicalCode, { ...e, code: canonicalCode });
    }
  });

  const displayEvents = [...groupMap.values()].sort((a, b) => {
    const aIdx = successCodesOrder.indexOf(a.code ?? -1);
    const bIdx = successCodesOrder.indexOf(b.code ?? -1);
    if (aIdx !== -1 && bIdx !== -1) return aIdx - bIdx;
    if (aIdx !== -1) return -1;
    if (bIdx !== -1) return 1;
    return 0;
  });

  return (
    <Box
      ref={scrollRef}
      mt={2}
      borderTop="1px dashed #E2E8F0"
      pt={2}
      maxH="96px"
      overflow="auto"
      w="100%"
    >
      {displayEvents.map((stats, i) => {
        const code = stats.code ?? 400;
        const successDiagnostic = SUCCESS_DIAGNOSTICS[code];
        const isError = code >= 400 && !successDiagnostic;
        const textColor = isError
          ? "#E53E3E"
          : (successDiagnostic?.colors?.title ?? "#38A169");
        const badgeScheme = isError
          ? "red"
          : (successDiagnostic?.colors?.badgeScheme ?? "green");
        const displayLabel =
          successDiagnostic?.title ?? stats.statusDescription;

        return (
          <HStack
            alignItems="center"
            h="28px"
            key={i}
            justify="space-between"
            px={1}
            py="1px"
            cursor="pointer"
            borderRadius="6px"
            _hover={{ bg: "#F7FAFC" }}
            className="nodrag nopan"
          >
            <Tooltip
              label={stats.statusDescription}
              bg="white"
              border="1px solid var(--chakra-colors-custom-gray)"
              color="prymary.500"
              boxShadow="none"
              borderRadius="10px"
            >
              <Text fontSize="xs" color={textColor} maxW="120px" isTruncated>
                {displayLabel}
              </Text>
            </Tooltip>
            <Flex gap="2" align="center">
              <Badge colorScheme={badgeScheme} fontSize="xs">
                {stats.total}
              </Badge>

              <ActionButtons
                stats={stats}
                pipelineData={nodeData.pipelineData}
              />
            </Flex>
          </HStack>
        );
      })}
      <ModalDetalhesErro />
    </Box>
  );
}
